import { FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { Modal } from "@/components";
import { useSnackBar } from "@/utils/useSnackbar";

import { useTriggerDynamicsResyncMutation } from "@/pages/Private/redux/dynamics/dynamics.api";

const COOLDOWN_MS = 30000;

const getBackendMessage = (error: unknown): string | undefined => {
	if (typeof error === "object" && error !== null && "data" in error) {
		const data = (error as { data?: { message?: string } }).data;

		if (data && typeof data.message === "string") {
			return data.message;
		}
	}

	return undefined;
};

export const DynamicsResyncSection: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`company.msdynamics.resync.${key}`);
	const { handleOpenSnackbar, renderMessageSnackbar } = useSnackBar();

	const [isConfirmOpen, setIsConfirmOpen] = useState<boolean>(false);
	const [isCooldown, setIsCooldown] = useState<boolean>(false);

	const [triggerResync, { isLoading }] = useTriggerDynamicsResyncMutation();

	useEffect(() => {
		if (!isCooldown) {
			return;
		}

		const timeout = window.setTimeout(() => setIsCooldown(false), COOLDOWN_MS);

		return () => window.clearTimeout(timeout);
	}, [isCooldown]);

	const handleConfirmResync = async () => {
		try {
			const response = await triggerResync().unwrap();

			setIsConfirmOpen(false);
			setIsCooldown(true);

			if (response.queuedCount === 0) {
				handleOpenSnackbar(ts("empty"));
			} else {
				handleOpenSnackbar(t("company.msdynamics.resync.success", { count: response.queuedCount }));
			}
		} catch (error) {
			setIsConfirmOpen(false);
			handleOpenSnackbar(getBackendMessage(error) ?? ts("error"));
		}
	};

	return (
		<>
			<div className="mt-6 pt-6 border-t border-border-light">
				<div className="text-sm font-medium text-text mb-2">{ts("title")}</div>
				<div className="text-sm text-text-meta mb-3">{ts("description")}</div>
				<div className="w-fit">
					<Button
						color={ButtonColor.ACTION}
						disabled={isLoading || isCooldown}
						isLoading={isLoading}
						size={ButtonSize.S}
						testId="dynamics-resync-button"
						title={ts("button")}
						onClick={() => setIsConfirmOpen(true)}
					/>
				</div>
			</div>

			{isConfirmOpen && (
				<Modal
					handleClose={() => setIsConfirmOpen(false)}
					handleSave={handleConfirmResync}
					isLoading={isLoading}
					isOpened={isConfirmOpen}
					submitButtonText={ts("confirmCta")}
					title={ts("confirmTitle")}
				>
					<div className="text-sm text-text">{ts("confirmBody")}</div>
				</Modal>
			)}

			{renderMessageSnackbar()}
		</>
	);
};
